<script setup lang="ts">
useHead({
  title: 'Artikel - Enrico Reinsdorf',
  meta: [
    {
      name: 'description',
      content: 'Fachartikel von Enrico Reinsdorf über UX Design, User Research, Design Systems und nutzerzentrierte Produktentwicklung.'
    }
  ]
})

const { data: articles } = await useAsyncData('articles', () =>
  queryCollection('articles')
    .where('draft', '=', false)
    .order('date', 'DESC')
    .all()
)

const selectedTag = ref<string | null>(null)

const allTags = computed(() => {
  const tags = new Set<string>()
  articles.value?.forEach(article => {
    article.tags?.forEach((tag: string) => tags.add(tag))
  })
  return Array.from(tags).sort()
})

const filteredArticles = computed(() => {
  if (!selectedTag.value) return articles.value
  return articles.value?.filter(article =>
    article.tags?.includes(selectedTag.value!)
  )
})

function readingTime(body: unknown): number {
  const text = JSON.stringify(body)
  const wordCount = text.split(/\s+/).length
  return Math.ceil(wordCount / 200)
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Page Header -->
    <section class="section-padding bg-gray-50 border-b border-gray-200">
      <div class="container-width px-4 md:px-8 lg:px-16">
        <h1 class="text-5xl md:text-6xl font-bold text-dark-900 mb-6">
          Artikel
        </h1>
        <p class="text-xl text-dark-600 max-w-2xl">
          Gedanken und Erkenntnisse zu UX Design, User Research und nutzerzentrierter Produktentwicklung.
        </p>
      </div>
    </section>

    <!-- Filter -->
    <section class="border-b border-gray-200">
      <div class="container-width px-4 md:px-8 lg:px-16 py-6">
        <div class="flex flex-wrap gap-2">
          <button
            @click="selectedTag = null"
            :class="[
              'px-4 py-2 rounded-full text-sm font-medium transition-colors',
              !selectedTag
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-dark-600 hover:bg-gray-200'
            ]"
          >
            Alle
          </button>
          <button
            v-for="tag in allTags"
            :key="tag"
            @click="selectedTag = tag === selectedTag ? null : tag"
            :class="[
              'px-4 py-2 rounded-full text-sm font-medium transition-colors',
              selectedTag === tag
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-dark-600 hover:bg-gray-200'
            ]"
          >
            {{ tag }}
          </button>
        </div>
      </div>
    </section>

    <!-- Articles List -->
    <section class="section-padding">
      <div class="container-width px-4 md:px-8 lg:px-16">
        <div v-if="filteredArticles && filteredArticles.length > 0" class="space-y-8">
          <article
            v-for="article in filteredArticles"
            :key="article.path"
            class="group border border-gray-200 rounded-xl p-8 hover:border-primary-300 hover:shadow-sm transition-all"
          >
            <NuxtLink :to="article.path">
              <!-- Meta -->
              <div class="flex flex-wrap items-center gap-4 text-sm text-dark-500 mb-4">
                <span>{{ formatDate(article.date) }}</span>
                <span>·</span>
                <span>{{ readingTime(article.body) }} Min. Lesezeit</span>
              </div>

              <!-- Title -->
              <h2 class="text-2xl font-bold text-dark-900 group-hover:text-primary-500 transition-colors mb-3">
                {{ article.title }}
              </h2>

              <!-- Description -->
              <p class="text-dark-600 leading-relaxed mb-6">
                {{ article.description }}
              </p>

              <!-- Tags -->
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="tag in article.tags"
                  :key="tag"
                  class="px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full font-medium"
                >
                  {{ tag }}
                </span>
              </div>
            </NuxtLink>
          </article>
        </div>

        <div v-else class="py-20 text-center text-dark-500">
          <p class="text-lg">Keine Artikel in dieser Kategorie gefunden.</p>
        </div>
      </div>
    </section>
  </div>
</template>
